/**
 * Daily-cap tier multipliers — a LOCAL MIRROR of the canonical constant.
 *
 * Canonical source: `GR_DAILY_CAP_TIER_MULTIPLIERS` in
 * `packages/db-types/src/game-rewards.ts`. The client package cannot import
 * `@fias/db-types` (same constraint documented in
 * `client/src/components/fias-ai/types.ts`), so this is a copy — and a copy of
 * a number that decides what a player may earn is exactly the kind that drifts
 * silently and then shows a wrong figure to the admin who is tuning it.
 *
 * `dailyCapTiers.test.ts` reads the canonical file and fails if these diverge,
 * so drift is a red build rather than a wrong number on screen.
 *
 * The stored cap is the FREE-player number; every other tier scales up from it.
 */
export const DAILY_CAP_TIER_MULTIPLIERS = {
  free: 1,
  bronze: 1.5,
  silver: 2,
  gold: 3,
} as const;

export type DailyCapTierName = keyof typeof DAILY_CAP_TIER_MULTIPLIERS;

/** Tier order as rendered in the admin table and the dialog preview. */
export const DAILY_CAP_TIER_ORDER: readonly DailyCapTierName[] = [
  'free',
  'bronze',
  'silver',
  'gold',
];

/**
 * Effective cap for one tier, or `null` when the stored value is the `0`
 * unlimited sentinel.
 *
 * Rounds DOWN, matching `resolveEffectiveDailyCap` server-side — rounding up
 * would let a tier exceed the number shown in the very table this feeds.
 */
export function effectiveCapForTier(
  storedMaxAmountPerDay: number,
  tier: DailyCapTierName,
): number | null {
  if (!Number.isFinite(storedMaxAmountPerDay) || storedMaxAmountPerDay <= 0) return null;
  return Math.floor(storedMaxAmountPerDay * DAILY_CAP_TIER_MULTIPLIERS[tier]);
}
